import { useState } from 'react'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
  Spinner,
} from '@/components/ui'
import { useApprovals, useApproveApproval, useRejectApproval } from '@/hooks'
import type { Approval } from '@/types/toolFramework'

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'expired' | 'all'

function statusVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === 'pending') return 'secondary'
  if (status === 'approved') return 'default'
  return 'outline'
}

interface ApprovalCardProps {
  approval: Approval
}

function ApprovalCard({ approval }: ApprovalCardProps) {
  const [showArgs, setShowArgs] = useState(false)
  const { addToast } = useToast()

  const { mutate: approve, isPending: isApproving } = useApproveApproval()
  const { mutate: reject, isPending: isRejecting } = useRejectApproval()

  const isBusy = isApproving || isRejecting

  function handleApprove() {
    approve(approval.id, {
      onSuccess: () => addToast({ type: 'success', title: 'Approval granted' }),
      onError: (err) =>
        addToast({
          type: 'error',
          title: 'Approval failed',
          description: err instanceof Error ? err.message : undefined,
        }),
    })
  }

  function handleReject() {
    reject(approval.id, {
      onSuccess: () => addToast({ type: 'success', title: 'Approval rejected' }),
      onError: (err) =>
        addToast({
          type: 'error',
          title: 'Rejection failed',
          description: err instanceof Error ? err.message : undefined,
        }),
    })
  }

  return (
    <Card className="p-4 gap-0 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-sm font-medium text-foreground break-all">
          {approval.capability_id}
        </span>
        <Badge variant={statusVariant(approval.status)} className="shrink-0">
          {approval.status}
        </Badge>
      </div>

      {/* Metadata */}
      <div className="text-xs text-muted-foreground space-y-0.5">
        <p>Requested {new Date(approval.created_at).toLocaleString()}</p>
        <p>Expires {new Date(approval.expires_at).toLocaleString()}</p>
      </div>

      {/* Reason */}
      {approval.reason && (
        <p className="text-sm text-foreground">
          <span className="font-medium">Reason:</span> {approval.reason}
        </p>
      )}

      {/* Args toggle */}
      <div>
        <button
          onClick={() => setShowArgs((v) => !v)}
          className="text-xs text-primary hover:underline"
          data-testid={`show-args-${approval.id}`}
        >
          {showArgs ? 'Hide args' : 'Show args'}
        </button>
        {showArgs && (
          <pre className="mt-1 whitespace-pre-wrap wrap-break-word text-xs bg-muted rounded-sm p-2">
            {JSON.stringify(approval.args_redacted, null, 2)}
          </pre>
        )}
      </div>

      {/* Actions */}
      {approval.status === 'pending' ? (
        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <Button
            size="sm"
            onClick={handleApprove}
            disabled={isBusy}
            isLoading={isApproving}
            data-testid={`approve-${approval.id}`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleReject}
            disabled={isBusy}
            isLoading={isRejecting}
            data-testid={`reject-${approval.id}`}
          >
            Reject
          </Button>
        </div>
      ) : (
        approval.resolved_at && (
          <p className="text-xs text-muted-foreground pt-1 border-t border-border">
            Resolved {new Date(approval.resolved_at).toLocaleString()}
            {approval.resolved_by_user_id ? ` by ${approval.resolved_by_user_id}` : ''}
          </p>
        )
      )}
    </Card>
  )
}

export function ApprovalQueue() {
  const [status, setStatus] = useState<StatusFilter>('pending')

  const { data, isLoading, error } = useApprovals({
    status: status === 'all' ? undefined : status,
  })

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="w-44" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load approvals'}
          </AlertDescription>
        </Alert>
      )}

      {/* Empty */}
      {!isLoading && !error && data?.length === 0 && (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No approvals found.
        </div>
      )}

      {/* List */}
      {!isLoading && !error && data && data.length > 0 && (
        <div className="space-y-4">
          {data.map((approval) => (
            <ApprovalCard key={approval.id} approval={approval} />
          ))}
        </div>
      )}
    </div>
  )
}
